/**
 * The Settings time zone picker: every zone this browser knows, grouped by
 * region and named for a parent ("Chicago (Central Time)"), while the value
 * saved stays the IANA name ("America/Chicago").
 */

export interface ZoneOption {
  value: string;
  label: string;
}

export interface ZoneGroup {
  region: string;
  zones: ZoneOption[];
}

/**
 * Old names that Safari and Chrome still list (CLDR's), and the current ones
 * phones report: without this, a parent in Kyiv sees "Kiev" and "Kyiv" both.
 */
const RENAMED: Record<string, string> = {
  "Africa/Asmera": "Africa/Asmara",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires",
  "America/Catamarca": "America/Argentina/Catamarca",
  "America/Coral_Harbour": "America/Atikokan",
  "America/Cordoba": "America/Argentina/Cordoba",
  "America/Godthab": "America/Nuuk",
  "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Jujuy": "America/Argentina/Jujuy",
  "America/Louisville": "America/Kentucky/Louisville",
  "America/Mendoza": "America/Argentina/Mendoza",
  "Asia/Calcutta": "Asia/Kolkata",
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Rangoon": "Asia/Yangon",
  "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Atlantic/Faeroe": "Atlantic/Faroe",
  "Europe/Kiev": "Europe/Kyiv",
  "Pacific/Enderbury": "Pacific/Kanton",
  "Pacific/Ponape": "Pacific/Pohnpei",
  "Pacific/Truk": "Pacific/Chuuk",
};

/** The current IANA name for a zone a browser may list or report under an old one. */
export const currentZoneName = (zone: string): string => RENAMED[zone] ?? zone;

const REGION_LABEL: Record<string, string> = { America: "Americas", Indian: "Indian Ocean" };
const OTHER = "Other";

/** "Central Time" for America/Chicago, or null where the browser has no name. */
function genericName(zone: string): string | null {
  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longGeneric" })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName")?.value ?? null
    );
  } catch {
    return null;
  }
}

// One formatter per zone is slow on a phone: label each zone once.
const labels = new Map<string, string>();

/**
 * "Chicago (Central Time)", "Buenos Aires, Argentina (Argentina Standard
 * Time)", "UTC": the place, smallest first, then the zone's own name.
 */
export function zoneLabel(zone: string): string {
  const cached = labels.get(zone);
  if (cached) return cached;
  const [, ...places] = zone.split("/");
  let label = zone;
  if (places.length > 0) {
    const place = places.reverse().join(", ").replaceAll("_", " ");
    const name = genericName(zone);
    label = name ? `${place} (${name})` : place;
  }
  labels.set(zone, label);
  return label;
}

/**
 * `zones` (the browser's list) under their current names, without the
 * fixed-offset Etc/ zones, grouped by region in A–Z order with places A–Z.
 * UTC is always one (Chrome's list leaves it out), and each of `keep` (the
 * zone on show) joins its region, so a saved zone the browser doesn't list
 * still shows.
 */
export function zoneGroups(zones: readonly string[], keep: readonly string[] = []): ZoneGroup[] {
  const names = new Set(zones.map(currentZoneName).filter((z) => !z.startsWith("Etc/")));
  for (const zone of ["UTC", ...keep]) if (zone) names.add(zone);
  const byRegion = new Map<string, ZoneOption[]>();
  for (const value of names) {
    const prefix = value.includes("/") ? value.slice(0, value.indexOf("/")) : OTHER;
    const region = REGION_LABEL[prefix] ?? prefix;
    const group = byRegion.get(region) ?? [];
    group.push({ value, label: zoneLabel(value) });
    byRegion.set(region, group);
  }
  return [...byRegion]
    .sort(([a], [b]) => (a === OTHER ? 1 : b === OTHER ? -1 : a.localeCompare(b)))
    .map(([region, options]) => ({
      region,
      zones: options.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

/** The zones this browser knows, or null where it can't list them (Safari before 15.4). */
export function browserZones(): string[] | null {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return null;
  }
}
